import { useState, useEffect, useCallback } from "react";
import { formatPhone, formatDate } from "../lib/format";
import { Lock, LogOut, Inbox, TrendingDown, Download, X, MapPin, CheckCircle2 } from "lucide-react";
import PricingTuner from "./PricingTuner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CLASS = {
  booked: "bg-sage/50 text-charcoal",
  completed: "bg-violet/15 text-violet",
  contacted: "bg-lavender text-violet",
  archived: "bg-charcoal/10 text-charcoal/50",
  quote: "bg-stone/50 text-charcoal/70",
};
const statusClass = (s) => STATUS_CLASS[s] || STATUS_CLASS.quote;

const CSV_COLS = [
  "name", "email", "phone", "address", "city", "zip", "urgency", "estimate_price",
  "quoted_price", "payment_method", "payment_status", "payment_amount", "tip_amount",
  "confidence", "detected_items", "status", "pickup_date", "pickup_window",
  "photo_count", "created_at",
];
const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("milla_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [revenue, setRevenue] = useState(null);
  const [funnelDays, setFunnelDays] = useState(30);
  const [leadFilter, setLeadFilter] = useState("active");
  const [sortBy, setSortBy] = useState("newest");
  const [routeRange, setRouteRange] = useState("all");
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("leads");
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!selected?.id || !selected.photo_count) { setPhotos([]); return; }
    let active = true;
    setPhotosLoading(true);
    fetch(`${API}/admin/leads/${selected.id}/photos`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { photos: [] }))
      .then((d) => { if (active) setPhotos(d.photos || []); })
      .catch(() => { if (active) setPhotos([]); })
      .finally(() => { if (active) setPhotosLoading(false); });
    return () => { active = false; };
  }, [selected?.id, selected?.photo_count, token]);

  const load = useCallback(async (tk) => {
    try {
      const [lr, fr] = await Promise.all([
        fetch(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${tk}` } }),
        fetch(`${API}/admin/funnel?days=${funnelDays}`, { headers: { Authorization: `Bearer ${tk}` } }),
      ]);
      if (lr.status === 401 || fr.status === 401) {
        setToken("");
        localStorage.removeItem("milla_admin_token");
        return;
      }
      const ld = await lr.json();
      const fd = await fr.json();
      setLeads(ld.leads || []);
      setFunnel(fd.funnel || []);
      setStatuses(fd.statuses || {});
      setRevenue(fd.revenue || null);
    } catch (e) {
      setError("Could not load data.");
    }
  }, [funnelDays]);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        setError("Incorrect password");
        return;
      }
      const data = await res.json();
      localStorage.setItem("milla_admin_token", data.token);
      setToken(data.token);
    } catch (e) {
      setError("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("milla_admin_token");
    setToken("");
    setLeads([]);
  };

  const updateStatus = async (leadId, status) => {
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, status } : l)));
    try {
      const res = await fetch(`${API}/admin/leads/${leadId}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("failed");
      load(token);
    } catch (e) {
      setLeads(prev);
      setError("Could not update status.");
    }
  };

  const [quotePrice, setQuotePrice] = useState("");
  const [quoteNote, setQuoteNote] = useState("");
  const [quoteSending, setQuoteSending] = useState(false);
  const [quoteSent, setQuoteSent] = useState(null);

  const sendQuote = async (leadId) => {
    if (!quotePrice) return;
    setQuoteSending(true);
    setQuoteSent(null);
    try {
      const res = await fetch(`${API}/admin/leads/${leadId}/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ price: Number(quotePrice), note: quoteNote, valid_days: 7, base_url: window.location.origin }),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setQuoteSent(data.quote_url);
      setSelected((p) => (p ? { ...p, status: "quoted" } : p));
      setQuotePrice("");
      setQuoteNote("");
      load(token);
    } catch (e) {
      setError("Could not send quote.");
    } finally {
      setQuoteSending(false);
    }
  };

  const exportCsv = () => {
    const rows = leads.filter((l) => (LEAD_FILTERS.find((f) => f[0] === leadFilter) || LEAD_FILTERS[0])[2](l.status));
    const header = CSV_COLS.join(",");
    const body = rows.map((l) => CSV_COLS.map((c) => csvCell(l[c])).join(",")).join("\n");
    const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `milla-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas px-5">
        <form onSubmit={login} className="w-full max-w-sm rounded-[2rem] bg-white/70 p-9 shadow-[0_30px_80px_-40px_rgba(23,23,23,0.4)]">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-lavender text-violet">
            <Lock size={22} />
          </span>
          <h1 className="mt-5 font-logo font-semibold text-3xl text-violet lowercase">milla admin</h1>
          <p className="mt-1 text-charcoal/55">Enter the admin password to continue.</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            data-testid="admin-password"
            className="mt-6 w-full rounded-2xl border border-stone bg-white/70 px-4 py-3 text-charcoal outline-none focus:border-violet"
          />
          {error && <p className="mt-3 text-sm text-red-600" data-testid="admin-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="mt-5 w-full rounded-full bg-violet px-6 py-3.5 font-semibold text-white transition-colors hover:bg-violet-deep disabled:opacity-60"
          >
            {loading ? "Checking…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  const maxFunnel = Math.max(1, ...funnel.map((f) => f.count));

  const LEAD_FILTERS = [
    ["active", "Active", (s) => !["completed", "archived"].includes(s)],
    ["quote", "New", (s) => s === "quote"],
    ["manual_review", "Needs quote", (s) => s === "manual_review"],
    ["quoted", "Quoted", (s) => s === "quoted"],
    ["booked", "Booked", (s) => s === "booked"],
    ["contacted", "Contacted", (s) => s === "contacted"],
    ["completed", "Completed", (s) => s === "completed"],
    ["archived", "Archived", (s) => s === "archived"],
    ["all", "All", () => true],
  ];
  const activeFilter = LEAD_FILTERS.find((f) => f[0] === leadFilter) || LEAD_FILTERS[0];
  const WINDOW_ORDER = ["Morning", "Afternoon", "Evening"];
  const winIdx = (w) => {
    const i = WINDOW_ORDER.findIndex((x) => (w || "").startsWith(x));
    return i < 0 ? 9 : i;
  };
  const pickupKey = (l) => {
    if (!l.pickup_date) return "";
    return `${l.pickup_date}#${winIdx(l.pickup_window)}`;
  };
  const filteredLeads = leads
    .filter((l) => activeFilter[2](l.status))
    .sort((a, b) => {
      if (sortBy === "newest") {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      // pickup sorting — leads with no pickup date sort to the bottom
      const ak = pickupKey(a);
      const bk = pickupKey(b);
      if (!ak && !bk) return 0;
      if (!ak) return 1;
      if (!bk) return -1;
      return sortBy === "pickup_soon" ? ak.localeCompare(bk) : bk.localeCompare(ak);
    });

  // route view: confirmed (booked) pickups only, grouped by date
  const todayIso = new Date().toISOString().slice(0, 10);
  const weekEndIso = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 6);
    return d.toISOString().slice(0, 10);
  })();
  const routeLeads = leads
    .filter((l) => {
      if (!l.pickup_date || l.pickup_date < todayIso) return false;
      if (l.status !== "booked") return false;
      if (routeRange === "today") return l.pickup_date === todayIso;
      if (routeRange === "week") return l.pickup_date <= weekEndIso;
      return true;
    })
    .sort((a, b) => pickupKey(a).localeCompare(pickupKey(b)));
  const routeGroups = routeLeads.reduce((acc, l) => {
    (acc[l.pickup_date] = acc[l.pickup_date] || []).push(l);
    return acc;
  }, {});
  const routeDates = Object.keys(routeGroups).sort();
  const fmtRouteDate = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
  };
  const dayMapUrl = (stops) => {
    const pts = stops
      .map((l) => `${l.address || ""} ${l.city || ""} ${l.zip || ""}`.trim())
      .filter(Boolean)
      .map(encodeURIComponent);
    return pts.length ? `https://www.google.com/maps/dir/${pts.join("/")}` : "";
  };

  return (
    <div className="min-h-screen bg-canvas px-5 py-10 sm:px-8">
      <div className="mx-auto max-w-6xl">
        <header className="flex items-center justify-between">
          <h1 className="font-logo font-semibold text-3xl text-violet lowercase">milla admin</h1>
          <button onClick={logout} className="inline-flex items-center gap-2 rounded-full bg-white/70 px-5 py-2.5 text-sm font-semibold text-charcoal hover:bg-stone/60">
            <LogOut size={15} /> Sign out
          </button>
        </header>

        {/* tabs */}
        <div className="mt-6 inline-flex rounded-full bg-white/60 p-1">
          {[["leads", "Leads"], ["route", "Route"], ["pricing", "Pricing"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`tab-${k}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-violet text-white" : "text-charcoal/70 hover:text-charcoal"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "pricing" ? (
          <div className="mt-6"><PricingTuner token={token} /></div>
        ) : tab === "route" ? (
          <section className="mt-8" data-testid="route-view">
            <div className="flex items-center gap-2 text-charcoal/70">
              <MapPin size={18} className="text-violet" />
              <h2 className="font-display text-2xl text-charcoal">Route plan</h2>
            </div>
            <p className="mt-1 text-sm text-charcoal/55">Upcoming booked pickups, grouped by day and ordered by time window.</p>
            <div className="mt-4 inline-flex rounded-full bg-white/60 p-1" data-testid="route-range">
              {[["today", "Today"], ["week", "This week"], ["all", "All upcoming"]].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setRouteRange(k)}
                  data-testid={`route-range-${k}`}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${routeRange === k ? "bg-violet text-white" : "text-charcoal/60 hover:text-charcoal"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            {routeDates.length === 0 ? (
              <p className="mt-8 rounded-[2rem] bg-white/60 p-6 text-center text-charcoal/45 sm:p-9">
                {routeRange === "today" ? "No pickups scheduled for today." : routeRange === "week" ? "No pickups in the next 7 days." : "No upcoming pickups scheduled."}
              </p>
            ) : (
              <div className="mt-6 space-y-5 sm:space-y-6">
                {routeDates.map((date) => (
                  <div key={date} className="rounded-[1.6rem] bg-white/60 p-4 sm:rounded-[2rem] sm:p-7" data-testid={`route-day-${date}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
                      <h3 className="font-display text-lg text-charcoal sm:text-xl">{fmtRouteDate(date)}</h3>
                      <div className="flex items-center gap-2">
                        {dayMapUrl(routeGroups[date]) && (
                          <a
                            href={dayMapUrl(routeGroups[date])}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`route-day-map-${date}`}
                            className="inline-flex items-center gap-1.5 rounded-full bg-violet px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-violet-deep sm:px-4 sm:text-sm"
                          >
                            <MapPin size={14} /> <span className="hidden sm:inline">Open full route</span><span className="sm:hidden">Route</span>
                          </a>
                        )}
                        <span className="rounded-full bg-canvas px-3 py-1 text-xs font-semibold text-charcoal/60 sm:text-sm">
                          {routeGroups[date].length} stop{routeGroups[date].length > 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 space-y-2.5 sm:space-y-3">
                      {routeGroups[date].map((l, i) => (
                        <div
                          key={l.id}
                          onClick={() => setSelected(l)}
                          data-testid={`route-stop-${l.id}`}
                          className="flex cursor-pointer items-start gap-3 rounded-2xl bg-canvas/70 p-3.5 transition-colors hover:bg-canvas sm:gap-4 sm:p-4"
                        >
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet text-xs font-bold text-white sm:h-8 sm:w-8 sm:text-sm">{i + 1}</span>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-charcoal">{l.name || "—"}</p>
                              <span className="rounded-full bg-lavender px-2.5 py-0.5 text-xs font-semibold text-violet">{l.pickup_window || "Any time"}</span>
                              {l.reschedule_count > 0 && (
                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">Rescheduled</span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-charcoal/65 break-words">{l.address || "No address on file"}{l.city ? `, ${l.city}` : ""}{l.zip ? ` · ${l.zip}` : ""}</p>
                            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-charcoal/50">
                              {l.phone && <span>{formatPhone(l.phone)}</span>}
                              {(l.quoted_price || l.estimate_price) && <span>{l.quoted_price || l.estimate_price}</span>}
                              {(l.address || l.city || l.zip) && (
                                <a
                                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${l.address || ""} ${l.city || ""} ${l.zip || ""}`.trim())}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`route-map-${l.id}`}
                                  className="font-semibold text-violet hover:underline"
                                >
                                  Open in Maps →
                                </a>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); updateStatus(l.id, "completed"); }}
                            data-testid={`route-complete-${l.id}`}
                            title="Mark completed"
                            className="flex shrink-0 items-center gap-1.5 self-center rounded-full border border-sage bg-white/70 px-3.5 py-2 text-xs font-semibold text-charcoal transition-colors hover:bg-sage/40"
                          >
                            <CheckCircle2 size={15} className="text-sage" /> Done
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        ) : (
        <>
        {/* revenue */}
        {revenue && (
          <section className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4" data-testid="revenue-stats">
            <div className="rounded-[1.6rem] bg-violet p-5 text-white sm:p-6">
              <p className="text-sm text-white/70">Revenue today</p>
              <p className="mt-1 font-display text-3xl tracking-tight sm:text-4xl" data-testid="revenue-today">
                ${revenue.today.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="rounded-[1.6rem] bg-white/60 p-5 sm:p-6">
              <p className="text-sm text-charcoal/55">Total collected</p>
              <p className="mt-1 font-display text-3xl tracking-tight text-charcoal sm:text-4xl" data-testid="revenue-total">
                ${revenue.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="rounded-[1.6rem] bg-white/60 p-5 sm:p-6">
              <p className="text-sm text-charcoal/55">Paid (card)</p>
              <p className="mt-1 font-display text-3xl tracking-tight text-violet sm:text-4xl" data-testid="revenue-paid-count">{revenue.paid_count}</p>
            </div>
            <div className="rounded-[1.6rem] bg-white/60 p-5 sm:p-6">
              <p className="text-sm text-charcoal/55">Cash on pickup</p>
              <p className="mt-1 font-display text-3xl tracking-tight text-charcoal sm:text-4xl" data-testid="revenue-cash-count">{revenue.cash_count}</p>
            </div>
          </section>
        )}

        {/* funnel */}
        <section className="mt-8 rounded-[2rem] bg-white/60 p-5 sm:p-9">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-charcoal/70">
              <TrendingDown size={18} className="text-violet" />
              <h2 className="font-display text-2xl text-charcoal">Quote funnel</h2>
            </div>
            <div className="inline-flex rounded-full bg-canvas p-1" data-testid="funnel-range">
              {[[7, "7d"], [30, "30d"], [0, "All"]].map(([d, label]) => (
                <button
                  key={d}
                  onClick={() => setFunnelDays(d)}
                  data-testid={`funnel-range-${label}`}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${funnelDays === d ? "bg-violet text-white" : "text-charcoal/60 hover:text-charcoal"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-6 space-y-4">
            {funnel.map((f, i) => {
              const prev = i > 0 ? funnel[i - 1].count : f.count;
              const drop = prev > 0 ? Math.round((1 - f.count / prev) * 100) : 0;
              return (
                <div key={f.step} className="flex items-center gap-3 sm:gap-4">
                  <span className="w-20 shrink-0 text-xs text-charcoal/65 sm:w-36 sm:text-sm">{f.step}</span>
                  <div className="h-9 flex-1 overflow-hidden rounded-full bg-stone/40">
                    <div
                      className="flex h-full items-center justify-end rounded-full bg-violet px-3 text-sm font-semibold text-white transition-all"
                      style={{ width: `${Math.max(8, (f.count / maxFunnel) * 100)}%` }}
                    >
                      {f.count}
                    </div>
                  </div>
                  <span className="w-16 shrink-0 text-right text-xs text-charcoal/45">
                    {i > 0 && drop > 0 ? `−${drop}%` : ""}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            {Object.entries(statuses).map(([k, v]) => (
              <span key={k} className="rounded-full bg-canvas px-4 py-2 text-sm text-charcoal/70">
                <strong className="text-charcoal">{v}</strong> {k}
              </span>
            ))}
          </div>
        </section>

        {/* leads */}
        <section className="mt-6 rounded-[2rem] bg-white/60 p-5 sm:p-9">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-charcoal/70">
              <Inbox size={18} className="text-violet" />
              <h2 className="font-display text-2xl text-charcoal">Leads ({filteredLeads.length})</h2>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="lead-sort-select"
                className="rounded-full border border-stone bg-white/70 px-3 py-2 text-sm font-semibold text-charcoal outline-none focus:border-violet"
              >
                <option value="newest">Newest</option>
                <option value="pickup_soon">Pickup soonest</option>
                <option value="pickup_late">Pickup latest</option>
              </select>
              <button
                onClick={exportCsv}
                disabled={filteredLeads.length === 0}
                data-testid="export-csv-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-2 text-sm font-semibold text-charcoal transition-colors hover:bg-stone/60 disabled:opacity-40"
              >
                <Download size={15} /> <span className="hidden sm:inline">Export CSV</span><span className="sm:hidden">CSV</span>
              </button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="lead-filters">
            {LEAD_FILTERS.map(([key, label, match]) => {
              const count = leads.filter((l) => match(l.status)).length;
              return (
                <button
                  key={key}
                  onClick={() => setLeadFilter(key)}
                  data-testid={`lead-filter-${key}`}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${leadFilter === key ? "bg-violet text-white" : "bg-canvas text-charcoal/60 hover:text-charcoal"}`}
                >
                  {label} <span className={leadFilter === key ? "text-white/70" : "text-charcoal/35"}>{count}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-5 hidden overflow-x-auto sm:block">
            <table className="w-full min-w-[880px] text-left text-sm" data-testid="admin-leads-table">
              <thead>
                <tr className="border-b border-stone text-xs uppercase tracking-wide text-charcoal/45">
                  <th className="py-3 pr-4">Name</th>
                  <th className="py-3 pr-4">Contact</th>
                  <th className="py-3 pr-4">ZIP</th>
                  <th className="py-3 pr-4">Estimate</th>
                  <th className="py-3 pr-4">Items</th>
                  <th className="py-3 pr-4">Pickup</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4">Action</th>
                  <th className="py-3 pr-4">When</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => setSelected(l)}
                    data-testid={`lead-row-${l.id}`}
                    className="cursor-pointer border-b border-stone/50 align-top transition-colors hover:bg-white/60"
                  >
                    <td className="py-3 pr-4 font-semibold text-charcoal">{l.name || "—"}</td>
                    <td className="py-3 pr-4 text-charcoal/70">
                      <div>{l.email || "—"}</div>
                      <div className="text-charcoal/45">{formatPhone(l.phone) || ""}</div>
                    </td>
                    <td className="py-3 pr-4 text-charcoal/70">{l.zip || "—"}</td>
                    <td className="py-3 pr-4 text-charcoal/70">
                      {l.estimate_price || "—"}{l.confidence ? ` · ${l.confidence}` : ""}
                    </td>
                    <td className="py-3 pr-4 max-w-[180px] text-charcoal/60">{l.detected_items || "—"}</td>
                    <td className="py-3 pr-4 text-charcoal/60">
                      {l.pickup_date ? formatDate(l.pickup_date) : "—"}
                      {l.pickup_window ? <div className="text-charcoal/45">{l.pickup_window}</div> : null}
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(l.status)}`}>
                        {l.status}
                      </span>
                      {l.payment_status === "paid" && (
                        <span className="ml-1.5 rounded-full bg-violet/15 px-2 py-1 text-xs font-semibold text-violet" data-testid={`pay-badge-${l.id}`}>
                          Paid{l.payment_amount ? ` ${l.payment_amount}` : ""}
                        </span>
                      )}
                      {l.payment_status === "cash_on_pickup" && (
                        <span className="ml-1.5 rounded-full bg-sage/50 px-2 py-1 text-xs font-semibold text-charcoal" data-testid={`pay-badge-${l.id}`}>
                          Cash
                        </span>
                      )}
                      {l.reschedule_count > 0 && (
                        <span className="ml-1.5 rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700" data-testid={`reschedule-badge-${l.id}`}>
                          Rescheduled{l.reschedule_count > 1 ? ` ×${l.reschedule_count}` : ""}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={["contacted", "completed", "archived"].includes(l.status) ? l.status : ""}
                        onChange={(e) => e.target.value && updateStatus(l.id, e.target.value)}
                        data-testid={`lead-status-select-${l.id}`}
                        className="rounded-full border border-stone bg-white/80 px-3 py-1.5 text-xs font-semibold text-charcoal outline-none focus:border-violet"
                      >
                        <option value="" disabled>Set status…</option>
                        <option value="contacted">Mark contacted</option>
                        <option value="completed">Mark completed</option>
                        <option value="archived">Archive</option>
                      </select>
                    </td>
                    <td className="py-3 pr-4 text-charcoal/45">
                      {l.created_at ? new Date(l.created_at).toLocaleDateString() : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredLeads.length === 0 && <p className="py-8 text-center text-charcoal/45">No leads in this view.</p>}
          </div>

          {/* mobile card list */}
          <div className="mt-5 space-y-3 sm:hidden" data-testid="admin-leads-cards">
            {filteredLeads.map((l) => (
              <div
                key={l.id}
                onClick={() => setSelected(l)}
                data-testid={`lead-card-${l.id}`}
                className="rounded-2xl bg-white/70 p-4 transition-colors active:bg-white"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-charcoal">{l.name || "—"}</p>
                    <p className="truncate text-sm text-charcoal/60">{l.email || "—"}</p>
                    {l.phone && <p className="text-sm text-charcoal/45">{formatPhone(l.phone)}</p>}
                  </div>
                  <span className="shrink-0 text-xs text-charcoal/40">{l.created_at ? new Date(l.created_at).toLocaleDateString() : ""}</span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(l.status)}`}>{l.status}</span>
                  {l.payment_status === "paid" && (
                    <span className="rounded-full bg-violet/15 px-2 py-1 text-xs font-semibold text-violet">Paid{l.payment_amount ? ` ${l.payment_amount}` : ""}</span>
                  )}
                  {l.payment_status === "cash_on_pickup" && (
                    <span className="rounded-full bg-sage/50 px-2 py-1 text-xs font-semibold text-charcoal">Cash</span>
                  )}
                  {l.reschedule_count > 0 && (
                    <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700">Rescheduled{l.reschedule_count > 1 ? ` ×${l.reschedule_count}` : ""}</span>
                  )}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-charcoal/40">Estimate</p>
                    <p className="text-charcoal/75">{l.estimate_price || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-charcoal/40">Pickup</p>
                    <p className="text-charcoal/75">{l.pickup_date ? formatDate(l.pickup_date) : "—"}{l.pickup_window ? <span className="block text-charcoal/45">{l.pickup_window}</span> : null}</p>
                  </div>
                </div>
                <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                  <select
                    value={["contacted", "completed", "archived"].includes(l.status) ? l.status : ""}
                    onChange={(e) => e.target.value && updateStatus(l.id, e.target.value)}
                    data-testid={`lead-card-status-select-${l.id}`}
                    className="w-full rounded-full border border-stone bg-white/80 px-3 py-2 text-xs font-semibold text-charcoal outline-none focus:border-violet"
                  >
                    <option value="" disabled>Set status…</option>
                    <option value="contacted">Mark contacted</option>
                    <option value="completed">Mark completed</option>
                    <option value="archived">Archive</option>
                  </select>
                </div>
              </div>
            ))}
            {filteredLeads.length === 0 && <p className="py-8 text-center text-charcoal/45">No leads in this view.</p>}
          </div>
        </section>
        </>
        )}
      </div>

      {/* lead detail drawer */}
      {selected && (tab === "leads" || tab === "route") && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-drawer">
          <div className="absolute inset-0 bg-charcoal/30 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative h-full w-full max-w-md overflow-y-auto bg-canvas p-5 shadow-[-20px_0_60px_-30px_rgba(23,23,23,0.5)] sm:p-9">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-2xl text-charcoal">{selected.name || "Lead"}</h3>
                <span className={`mt-2 inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(selected.status)}`}>
                  {selected.status}
                </span>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="lead-drawer-close"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-white/70 text-charcoal hover:bg-stone/60"
              >
                <X size={16} />
              </button>
            </div>
            <dl className="mt-6 space-y-3 text-sm sm:mt-7 sm:space-y-4">
              {[
                ["Email", selected.email],
                ["Phone", formatPhone(selected.phone)],
                ["Address", selected.address],
                ["City", selected.city],
                ["ZIP", selected.zip],
                ["Urgency", selected.urgency],
                ["Estimate", `${selected.estimate_price || "—"}${selected.confidence ? ` · ${selected.confidence}` : ""}`],
                ["Quoted price", selected.quoted_price],
                ["Payment", selected.payment_method ? `${selected.payment_method}${selected.payment_status ? ` · ${selected.payment_status}` : ""}${selected.payment_amount ? ` · ${selected.payment_amount}` : ""}${selected.tip_amount && selected.tip_amount !== "$0.00" ? ` (tip ${selected.tip_amount})` : ""}` : "—"],
                ["Detected items", selected.detected_items],
                ["Access", [
                  selected.inside && "Inside", selected.outside && "Outside/curbside",
                  selected.stairs && "Stairs", selected.no_elevator && "No elevator",
                  selected.long_carry && "Long carry", selected.heavy && "Heavy items",
                  selected.disassembly && "Disassembly", selected.parking && "Easy parking",
                ].filter(Boolean).join(", ")],
                ["Photos uploaded", selected.photo_count],
                ["Pickup date", formatDate(selected.pickup_date)],
                ["Pickup window", selected.pickup_window],
                ["Rescheduled", selected.reschedule_count > 0 ? `${selected.reschedule_count}× · last ${selected.rescheduled_at ? new Date(selected.rescheduled_at).toLocaleString() : "—"}` : "No"],
                ["Created", selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-3 border-b border-stone/50 pb-3 sm:gap-4">
                  <dt className="w-24 shrink-0 text-charcoal/50 sm:w-32">{k}</dt>
                  <dd className="flex-1 font-medium text-charcoal break-words">{v || "—"}</dd>
                </div>
              ))}
            </dl>

            {/* customer photos */}
            {selected.photo_count > 0 && (
              <div className="mt-7" data-testid="lead-photos">
                <p className="font-display text-lg text-charcoal">Customer photos ({selected.photo_count})</p>
                {photosLoading ? (
                  <p className="mt-2 text-sm text-charcoal/45">Loading photos…</p>
                ) : photos.length === 0 ? (
                  <p className="mt-2 text-sm text-charcoal/45">Photos are no longer available for this lead.</p>
                ) : (
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {photos.map((src, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setLightbox(src)}
                        data-testid={`lead-photo-${i}`}
                        className="group relative block overflow-hidden rounded-xl bg-stone/40"
                      >
                        <img
                          src={src}
                          alt={`Customer upload ${i + 1}`}
                          className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* send custom quote */}
            <div className="mt-7 rounded-2xl bg-lavender/40 p-5" data-testid="send-quote-panel">
              <p className="font-display text-lg text-charcoal">Send a custom quote</p>
              <p className="mt-0.5 text-xs text-charcoal/55">Emails &amp; texts the customer a one-tap accept link (valid 7 days).</p>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-lg font-semibold text-charcoal">$</span>
                <input
                  type="number"
                  value={quotePrice}
                  onChange={(e) => setQuotePrice(e.target.value)}
                  placeholder="1450"
                  data-testid="quote-price-input"
                  className="w-32 rounded-xl border border-stone bg-white/80 px-3 py-2 font-semibold text-charcoal outline-none focus:border-violet"
                />
              </div>
              <textarea
                value={quoteNote}
                onChange={(e) => setQuoteNote(e.target.value)}
                placeholder="Optional note to the customer (e.g. what's included)…"
                rows={2}
                data-testid="quote-note-input"
                className="mt-3 w-full rounded-xl border border-stone bg-white/80 px-3 py-2 text-sm text-charcoal outline-none focus:border-violet"
              />
              <button
                onClick={() => sendQuote(selected.id)}
                disabled={quoteSending || !quotePrice}
                data-testid="send-quote-btn"
                className="mt-3 inline-flex w-full items-center justify-center rounded-full bg-violet px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-deep disabled:opacity-50"
              >
                {quoteSending ? "Sending…" : "Send quote"}
              </button>
              {quoteSent && (
                <p className="mt-2 break-all text-xs text-sage" data-testid="quote-sent-msg">
                  Sent ✓ Link: {quoteSent}
                </p>
              )}
            </div>

            <p className="mt-6 text-xs font-medium uppercase tracking-wide text-charcoal/40">Update status</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {["contacted", "completed", "archived"].map((s) => (
                <button
                  key={s}
                  onClick={() => { updateStatus(selected.id, s); if (["completed", "archived"].includes(s)) { setSelected(null); } else { setSelected((p) => ({ ...p, status: s })); } }}
                  data-testid={`drawer-status-${s}`}
                  className="rounded-full bg-white/70 px-4 py-2 text-xs font-semibold text-charcoal transition-colors hover:bg-stone/60"
                >
                  Mark {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* photo lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-charcoal/80 p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
          data-testid="photo-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            data-testid="lightbox-close"
            className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-charcoal transition-colors hover:bg-white"
          >
            <X size={22} />
          </button>
          <img
            src={lightbox}
            alt="Customer upload full size"
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}
